<template>
  <div class="main block">
    <div>
      <div class="tabs is-boxed mb-3">
        <ul>
          <li :class="{ 'is-active': searchMode === 'id' }">
            <a @click="searchMode = 'id'">Card Number</a>
          </li>
          <li :class="{ 'is-active': searchMode === 'name' }">
            <a @click="switchToNameMode">Name</a>
          </li>
        </ul>
      </div>

      <div v-if="searchMode === 'id'">
        <div class="field has-addons">
          <div class="control is-expanded">
              <input
              v-model="beneficiaryID"
              class="input"
              type="number"
              inputmode="numeric"
              placeholder="Beneficiary Card Number">
          </div>
          <div class="control">
              <button
              :disabled="!beneficiaryID"
              @click="searchDelivery()"
              class="button is-info">
              Search
              </button>
          </div>
        </div>
      </div>

      <div v-if="searchMode === 'name'">
        <div class="field">
          <div class="control">
            <input
              v-model="searchName"
              class="input"
              type="text"
              placeholder="Type 2+ characters of beneficiary name">
          </div>
        </div>
        <div v-if="searchName.trim().length >= 2">
          <div v-if="nameSearchResults.length === 0" class="notification is-light">
            No matches found.
          </div>
          <div v-else class="box p-0">
            <table class="table is-fullwidth is-hoverable mb-0">
              <tbody>
                <tr v-for="match in nameSearchResults" :key="match.beneficiary_id">
                  <td>({{ match.beneficiary_id }}) {{ match.full_name }}</td>
                  <td class="has-text-right">
                    <button class="button is-small is-info" @click="selectNameResult(match)">
                      Select
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <base-spinner v-if="isLoading"></base-spinner>
      <div v-if="!!error" class="notification is-danger">
        <p>{{ error }}</p>
      </div>
      <div v-if="deliverySubmitted" class="notification is-success">
        Delivery Submitted Successfully!
      </div>
      <div v-if="deliveryFound">
        <span v-if="getDelivery.completed" class="tag is-large is-info">{{getDelivery.status}}</span>
        <span v-if="getDelivery.dry_bag" class="tag is-warning is-large">Dry Bag</span>
        <div class="box">
              <article class="media">
                  <div class="media-content">
                      <div class="content">
                          <p>
                              <strong>({{ getDelivery.beneficiary_id }}) {{ getDelivery.full_name }}</strong>
                              <br>
                              {{ getDelivery.unit }}, {{ getDelivery.block }} {{ getDelivery.street }}
                          </p>
                          {{getDelivery.quantity}} -
                        <span v-if="getDelivery.dry_bag">Dry Bag</span>
                        <span v-else>Normal Bag</span>
                        <div v-if="getDelivery.self_collection">
                          Self Collection
                        </div>
                        <div v-else-if="getDelivery.driving_delivery">
                          Driving Delivery
                        </div>
                        <div v-else>
                          Walking Delivery
                        </div>
                        <span v-if="getDelivery.completed && getDelivery.completed_at_time">
                            <b>Completed:</b> {{getDelivery.completed_at_time.toDate()}}
                          </span>
                      </div>
                  </div>
              </article>
          </div>
        <div v-if="!getDelivery.completed && !deliverySubmitted">
          <div class="field">
            <label class="label">Remarks</label>
            <div class="control">
              <input
                v-model="notes"
                class="input"
                :class="[isNotesRequired ? 'is-danger' : '']"
                type="text"
                placeholder="e.g., Not at home">
                <p v-if="isNotesRequired" class="help is-danger">Please specify the reason</p>
              <span class="icon is-left">
                <i class="fas fa-envelope"></i>
              </span>
              <span class="icon is-right">
                <i class="fas fa-check"></i>
              </span>
            </div>
          </div>
          <div class="field is-grouped is-grouped-centered">
            <p class="control">
              <button
                @click="saveDelivery('SELF_PICKUP')"
                class="button is-info">
                Self Pick-up
              </button>
            </p>
            <p class="control">
              <button
                @click="saveDelivery('DELIVERED')"
                class="button is-success">
                Delivered
              </button>
            </p>
            <p class="control">
              <button
                @click="saveDelivery('NOT_DELIVERED')"
                class="button is-danger">
                Not Delivered
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
    data() {
      return {
        isLoading: false,
        deliveryFound: false,
        deliverySubmitted: false,
        beneficiaryID: "",
        notes: "",
        isNotesRequired: false,
        error: null,
        searchMode: 'id',
        searchName: '',
        nameResultsLoaded: false,
      }
    },
    computed: {
      getCurrentFDID() {
        return this.$store.getters['fd/getCurrentFDID'];
      },
      getDelivery() {
        return this.$store.getters['delivery/getDelivery'];
      },
      nameSearchResults() {
        const term = this.searchName.trim().toLowerCase();
        if (term.length < 2) return [];
        const all = this.$store.getters['delivery/getPendingSelfCollection'] || [];
        return all.filter(d => d.full_name.toLowerCase().includes(term));
      },
    },
    methods: {
      async switchToNameMode() {
        this.searchMode = 'name';
        if (!this.nameResultsLoaded) {
          this.isLoading = true;
          try {
            await this.$store.dispatch('delivery/loadPendingSelfCollection', this.getCurrentFDID);
            this.nameResultsLoaded = true;
          } catch (error) {
            this.error = error.message || 'Failed to load beneficiary list.';
          }
          this.isLoading = false;
        }
      },
      async selectNameResult(match) {
        this.beneficiaryID = match.beneficiary_id;
        this.searchName = '';
        this.searchMode = 'id';
        await this.searchDelivery();
      },
      async saveDelivery(status) {
        if (status == 'NOT_DELIVERED' && !this.notes) {
          this.isNotesRequired = true;
        } else {
          this.isNotesRequired = false;
          this.error=null;
          this.isLoading = true;
          try {
            await this.$store.dispatch('delivery/updateDelivery', {
              fdID: this.getCurrentFDID,
              beneficiaryID: this.getDelivery.beneficiary_id,
              notes: this.notes,
              status: status,
              selfCollection: this.getDelivery.self_collection,
              drivingDelivery: this.getDelivery.driving_delivery
            });
            this.deliverySubmitted = true;
          } catch(error) {
            this.error = error.message || 'Something went wrong!';
            this.deliverySubmitted = false;
          }
          this.isLoading = false;
          this.notes = "";
        }
      },
      async searchDelivery() {
        this.error=null;
        this.isLoading = true;
        this.deliverySubmitted = false;
        try {
          await this.$store.dispatch('delivery/searchDelivery', {
            fdID: this.getCurrentFDID,
            beneficiaryID: this.beneficiaryID,
          });
          this.deliveryFound = true;
          this.beneficiaryID = "";
        } catch (error) {
          this.deliveryFound = false;
          this.error = error.message || 'Something went wrong!';
        }
        this.isLoading = false;
      }
    },
  };
</script>
